import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	Output,
} from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { isEmpty, isNull } from "lodash";
import isEqual from "lodash/isEqual";
import moment from "moment";
import { EMPTY, merge, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, map, skip, startWith, take } from "rxjs/operators";
import { SignOffPopup } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { toHttpErrorPayload } from "src/app/shared/utils/http-error-message";
import { ContainerRatingsService } from "../../../../services/base/container-ratings.services";
import { signOffNoteDraftValue } from "../../../utils/ts1-sign-off-note-draft.utils";

@Component({
	selector: "app-ts1-rating-step4-signoff",
	templateUrl: "./ts1-rating-step4-signoff.component.html",
	styleUrls: ["./ts1-rating-step4-signoff.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingStep4SignoffComponent implements OnInit, OnDestroy {
	@Input() signOffForm!: UntypedFormArray;
	@Output() previewSummary = new EventEmitter<void>();

	instructorSignaturePreview = "";
	instructorSignedAt = "";

	isOnline = true;

	private subs = new Subscription();

	constructor(
		public sharedService: SharedService,
		private dialog: MatDialog,
		private httpService: HttpService,
		private loadingService: LoaderService,
		private roleService: RoleService,
		private profileService: ProfileService,
		private containerRatingsService: ContainerRatingsService,
		private zone: NgZone,
	) {}

	ngOnInit(): void {
		this.subs.add(this.sharedService.getIsStatusOnline().subscribe((status) => (this.isOnline = status)));

		const stableSub = this.zone.onStable.pipe(take(1)).subscribe(() => {
			const hasValue = !!this.instructorLicenseNumber?.value;
			const useFlag = this.useInstructorLicense?.value ?? true;
			if (!hasValue) this.toggleLicenseNumber(useFlag, true);
			this.restoreSignaturePreviews();
			this.attachAutosave();
		});
		this.subs.add(stableSub);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	get instructorForm(): UntypedFormGroup {
		return (this.signOffForm?.at(0) as UntypedFormGroup) ?? (this.signOffForm?.controls[0] as UntypedFormGroup);
	}

	get candidateForms(): UntypedFormGroup[] {
		return (this.signOffForm?.controls ?? []) as UntypedFormGroup[];
	}

	get trainerName() {
		return this.instructorForm?.get("trainerName");
	}

	get trainerSignature() {
		return this.instructorForm?.get("trainerSignature");
	}

	get instructorLicenseNumber() {
		return this.instructorForm?.get("instructorLicenseNumber");
	}

	get useInstructorLicense() {
		return this.instructorForm?.get("useInstructorLicense");
	}

	get signingIsValid(): boolean {
		return this.candidateForms.every((form) => this.isCandidateSigningValid(form));
	}

	isCandidateSigningValid(form: UntypedFormGroup): boolean {
		const category = form.get("category");
		const sessionCheck = form.get("sessionCheck");
		const objectiveCheck = form.get("objectiveCheck");
		const clearanceCheck = form.get("clearanceCheck");
		return !!(category?.valid && sessionCheck?.valid && objectiveCheck?.valid && clearanceCheck?.valid);
	}

	get instructorHasSignaturePreview(): boolean {
		return !isEmpty(this.instructorSignaturePreview) && !isNull(this.instructorSignaturePreview);
	}

	private formatSignedAt(date = new Date()): string {
		return moment.utc(date).format("DD/MM/YY HH:mm:ss");
	}

	private restoreSignaturePreviews(): void {
		const trainerSig = this.trainerSignature?.value;
		if (trainerSig instanceof File || trainerSig instanceof Blob) {
			this.instructorSignaturePreview = URL.createObjectURL(trainerSig);
		}
	}

	toggleLicenseNumber(isLicenseNumber: boolean, silent = false): void {
		const fromProfile = isLicenseNumber
			? this.profileService?.profile?.pilot_license_number
			: this.profileService?.profile?.examiner_number;

		const licenseValue =
			fromProfile != null && String(fromProfile).trim().length > 0 ? fromProfile : this.instructorLicenseNumber?.value;

		this.patchInstructorFieldsOnAllRows(
			{
				useInstructorLicense: isLicenseNumber,
				instructorLicenseNumber: licenseValue,
			},
			!silent,
		);
	}

	private patchInstructorFieldsOnAllRows(
		patch: Partial<{ trainerSignature: unknown; useInstructorLicense: boolean; instructorLicenseNumber: unknown }>,
		emitEvent = true,
	): void {
		for (const form of this.candidateForms) {
			if (patch.useInstructorLicense !== undefined) {
				form.get("useInstructorLicense")?.patchValue(patch.useInstructorLicense, { emitEvent });
			}
			if (patch.instructorLicenseNumber !== undefined) {
				form.get("instructorLicenseNumber")?.patchValue(patch.instructorLicenseNumber, { emitEvent });
			}
			if (patch.trainerSignature !== undefined) {
				form.get("trainerSignature")?.patchValue(patch.trainerSignature, { emitEvent });
			}
		}
	}

	private attachAutosave(): void {
		const ctrl = (c: AbstractControl | null | undefined) => (c ? c.valueChanges : EMPTY);
		const currentInstructorPatch = () => ({
			useInstructorLicense: this.useInstructorLicense?.value,
			instructorLicenseNumber: this.instructorLicenseNumber?.value,
		});

		const changes$ = merge(
			ctrl(this.useInstructorLicense),
			ctrl(this.instructorLicenseNumber),
			ctrl(this.signOffForm),
		).pipe(
			map(currentInstructorPatch),
			startWith(currentInstructorPatch()),
			distinctUntilChanged((a, b) => isEqual(a, b)),
			skip(1),
			debounceTime(400),
		);

		this.subs.add(
			changes$.subscribe((patch) => {
				if (this.containerRatingsService.silenceAutosave) return;

				this.patchInstructorFieldsOnAllRows(
					{
						useInstructorLicense: patch.useInstructorLicense,
						instructorLicenseNumber: patch.instructorLicenseNumber,
					},
					false,
				);

				this.persistSignOffFormsToDraft(patch);
			}),
		);
	}

	private persistSignOffFormsToDraft(
		patch: Partial<{
			trainerSignature: unknown;
			useInstructorLicense: boolean;
			instructorLicenseNumber: unknown;
		}> = {},
	): void {
		const signOffDetails = this.containerRatingsService.draft?.signOffDetails;
		const candidateSignOffDetails = { ...(signOffDetails?.candidateSignOffDetails ?? {}) };

		for (const form of this.candidateForms) {
			const row = form.getRawValue?.() ?? form.value;
			const candidateId = row?.candidateId;
			if (!candidateId) continue;
			const saved = candidateSignOffDetails[candidateId] ?? {};
			const generalObservation = signOffNoteDraftValue(form.get("generalObservation"));
			const reminderNotes = signOffNoteDraftValue(form.get("reminderNotes"));
			candidateSignOffDetails[candidateId] = {
				...saved,
				ratingA: row.ratingA ?? saved.ratingA,
				ratingB: row.ratingB ?? saved.ratingB,
				ratingC: row.ratingC ?? saved.ratingC,
				finalRating: row.finalRating ?? saved.finalRating,
				category: row.category ?? saved.category ?? "",
				sessionCheck: row.sessionCheck !== undefined ? row.sessionCheck : saved.sessionCheck,
				objectiveCheck: row.objectiveCheck !== undefined ? row.objectiveCheck : saved.objectiveCheck,
				clearanceCheck: row.clearanceCheck !== undefined ? row.clearanceCheck : saved.clearanceCheck,
				useInstructorLicense:
					patch.useInstructorLicense !== undefined
						? patch.useInstructorLicense
						: (row.useInstructorLicense ?? saved.useInstructorLicense ?? true),
				instructorLicenseNumber:
					patch.instructorLicenseNumber !== undefined
						? patch.instructorLicenseNumber
						: (row.instructorLicenseNumber ?? saved.instructorLicenseNumber ?? ""),
				trainerName: this.trainerName?.value ?? row.trainerName ?? saved.trainerName,
				traineeName: row.traineeName ?? saved.traineeName ?? "",
				trainerSignature:
					patch.trainerSignature !== undefined
						? patch.trainerSignature
						: (row.trainerSignature ?? saved.trainerSignature ?? null),
				traineeSignature: row.traineeSignature ?? saved.traineeSignature ?? null,
				candidateName: row.traineeName ?? saved.candidateName ?? "",
				...(generalObservation !== undefined ? { generalObservation } : {}),
				...(reminderNotes !== undefined ? { reminderNotes } : {}),
				reminderNoteResponses: row.reminderNoteResponses ?? saved.reminderNoteResponses ?? [],
			};
		}

		this.containerRatingsService.updateSelectedDraft({
			signOffDetails: { candidateSignOffDetails },
			continuePage: 4,
		});
	}

	private showInstructorSignatureModal(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signature-pad-modal",
			height: "420px",
			width: "528px",
			hasBackdrop: true,
			data: {
				modalTitle: "Signature",
				modalName: "signaturePadModal",
				modalBtnConfirmText: "Submit",
				modalBtnCancelText: "Cancel",
				signatureData: {
					dataUrl: this.profileService.signaturePreview,
					file: this.profileService.signatureFile,
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result?.close) {
				const dataUrl = result?.signature?.dataUrl;
				const file = result?.signature?.file;

				this.patchInstructorFieldsOnAllRows({ trainerSignature: file });
				this.persistSignOffFormsToDraft({ trainerSignature: file });
				this.containerRatingsService.syncSelectedDraft();

				if (dataUrl) {
					this.instructorSignaturePreview = dataUrl;
					this.instructorSignedAt = this.formatSignedAt();
				}
			}
			sub.unsubscribe();
		});
	}

	/** Network drop uses the offline dialog. 503 uses Under Maintenance. Other HTTP failures keep their own message. */
	private onSignOffRequestError(err: unknown): void {
		this.loadingService.setLoading(false);
		const status = toHttpErrorPayload(err).status;
		if (status === 0) {
			this.containerRatingsService.blockSubmitIfOffline(true);
			return;
		}
		if (status === 503 && navigator.onLine) {
			this.containerRatingsService.presentMaintenanceModal(err);
			return;
		}
		this.containerRatingsService.presentSignOffHttpError(err);
	}

	async signOffModel(): Promise<void> {
		if (!navigator.onLine) {
			this.containerRatingsService.blockSubmitIfOffline(true);
			return;
		}

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signoff-modal",
			height: "400px",
			width: "460px",
			hasBackdrop: true,
			data: {
				modalName: "signOffModal",
				modalTitle: "Sign Off",
				modalContent: "",
				modalBtnConfirmText: "Sign",
				modalBtnCancelText: "Cancel",
				disableClose: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result) {
				const signOffData: SignOffPopup = {
					instructorId: this.roleService.userDetails.id,
					secretKey: result.signOffData.secretKey,
					staffNumber: result.signOffData.staffNumber,
				};

				this.loadingService.setLoading(true);

				this.httpService.instructorSignoffValidationQuiet(signOffData).subscribe({
					next: () => {
						this.loadingService.setLoading(false);
						this.showInstructorSignatureModal();
					},
					error: (err) => this.onSignOffRequestError(err),
				});
			}
			sub.unsubscribe();
		});
	}
}
